import React, { useState, useEffect } from 'react';
import { CardComponentProps } from '@/lib/cards';
import {
  listMailboxConnections,
  disconnectMailboxConnection,
  scanMailboxConnection,
  MailboxConnection,
} from '@/api/mailboxConnections';

/**
 * Gmail Connection Card
 * Connect / disconnect / rescan an additional Gmail mailbox.
 *
 * The connect flow is a top-level navigation to /auth/google/connect (the
 * backend builds the Google authorize URL and binds the flow to the session).
 * The callback redirects back to /profile with ?gmail=connected or
 * ?gmail_error=<code>, surfaced here as a one-shot notice.
 */
export const GmailConnectionCard: React.FC<CardComponentProps> = ({ onError }) => {
  const [connections, setConnections] = useState<MailboxConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [notice, setNotice] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  async function loadData() {
    try {
      setLoading(true);
      const result = await listMailboxConnections();
      setConnections(result.connections.filter((c) => c.provider === 'google'));
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onError]);

  // One-shot connect-flow result from the OAuth callback redirect.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('gmail');
    const error = params.get('gmail_error');
    if (connected === 'connected') {
      setNotice({ kind: 'success', text: 'Gmail connected successfully.' });
    } else if (error) {
      setNotice({ kind: 'error', text: `Gmail connection failed: ${error}` });
    }
    if (connected || error) {
      params.delete('gmail');
      params.delete('gmail_error');
      const qs = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
    }
  }, []);

  const handleConnect = () => {
    window.location.href = '/auth/google/connect';
  };

  const handleDisconnect = async (connection: MailboxConnection) => {
    try {
      setActionInProgress(true);
      await disconnectMailboxConnection(connection.id);
      await loadData();
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setActionInProgress(false);
    }
  };

  const handleScan = async (connection: MailboxConnection) => {
    try {
      setActionInProgress(true);
      const result = await scanMailboxConnection(connection.id);
      setNotice({ kind: 'success', text: `Queued ${result.queued} Gmail thread(s) for categorisation.` });
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setActionInProgress(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '1rem', color: '#666' }}>Loading Gmail connection...</div>;
  }

  const active = connections.find((c) => c.status === 'active');
  const needsReauth = connections.find((c) => c.status === 'reauth_required' || c.status === 'error');

  const buttonStyle = (background: string): React.CSSProperties => ({
    padding: '0.5rem 1rem',
    backgroundColor: background,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: actionInProgress ? 'not-allowed' : 'pointer',
    opacity: actionInProgress ? 0.6 : 1,
  });

  return (
    <div style={{ padding: '1rem' }}>
      {notice && (
        <div
          style={{
            marginBottom: '0.75rem',
            padding: '0.5rem 0.75rem',
            borderRadius: '4px',
            fontSize: '0.9rem',
            backgroundColor: notice.kind === 'success' ? '#d4edda' : '#f8d7da',
            color: notice.kind === 'success' ? '#155724' : '#721c24',
          }}
        >
          {notice.text}
        </div>
      )}

      {active && (
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ marginBottom: '0.25rem' }}>
            <strong>{active.email}</strong>{' — '}
            <span style={{ color: '#28a745' }}>connected</span>
          </div>
          <div style={{ fontSize: '0.85rem', color: '#666' }}>
            {active.lastSyncAt
              ? `Last checked ${new Date(active.lastSyncAt).toLocaleString()}`
              : 'Waiting for first poll — new mail is checked every few minutes.'}
          </div>
        </div>
      )}

      {needsReauth && !active && (
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ marginBottom: '0.25rem' }}>
            <strong>{needsReauth.email}</strong>{' — '}
            <span style={{ color: '#dc3545' }}>needs re-authorisation</span>
          </div>
          {needsReauth.lastError && (
            <div style={{ fontSize: '0.8rem', color: '#999' }}>{needsReauth.lastError}</div>
          )}
        </div>
      )}

      {!active && !needsReauth && (
        <div style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#666' }}>
          Connect a Gmail inbox to categorise its mail alongside your Outlook mailbox.
        </div>
      )}

      <div style={{ display: 'grid', gap: '0.5rem' }}>
        {!active && (
          <button onClick={handleConnect} disabled={actionInProgress} style={buttonStyle('#0066cc')}>
            {needsReauth ? 'Reconnect Gmail' : 'Connect Gmail'}
          </button>
        )}
        {active && (
          <>
            <button onClick={() => handleScan(active)} disabled={actionInProgress} style={buttonStyle('#0066cc')}>
              Scan Gmail inbox
            </button>
            <button onClick={() => handleDisconnect(active)} disabled={actionInProgress} style={buttonStyle('#dc3545')}>
              Disconnect Gmail
            </button>
          </>
        )}
      </div>
    </div>
  );
};

GmailConnectionCard.displayName = 'GmailConnectionCard';

export default GmailConnectionCard;
